/**
 * Outil « Cercle et équation » — calculs (pur).
 *
 * Cercle de centre Ω(a ; b) et de rayon r : (x − a)² + (y − b)² = r².
 * a, b sont des multiples de 0,5 (milieu de deux points entiers), r² un
 * multiple de 0,25 : tout s'écrit en décimaux exacts. Forme développée
 * x² + y² + px + qy + c = 0 et retour à la forme canonique en complétant
 * les carrés ; position d'un point ; cercle de diamètre [AB].
 */

import { rational } from '../../ui/exact';
import type { Pt } from '../../ui/frame';
import { isqrt, simplify } from '../root-square/roots';

/** nombre décimal exact (multiple de 0,25) en LaTeX : « 2 », « -0{,}5 » */
export function dec(v: number): string {
  return rational(Math.round(v * 4), 4).tex;
}

/** « x - 2 », « x + 3 », « x » */
function shift(name: 'x' | 'y', a: number): string {
  if (a === 0) return name;
  return a > 0 ? `${name} - ${dec(a)}` : `${name} + ${dec(-a)}`;
}

function square(name: 'x' | 'y', a: number): string {
  return a === 0 ? `${name}^2` : `(${shift(name, a)})^2`;
}

/** (x − a)² + (y − b)² = r² */
export function canonicalTex(a: number, b: number, r2: number): string {
  return `${square('x', a)} + ${square('y', b)} = ${dec(r2)}`;
}

/** coefficients de la forme développée x² + y² + px + qy + c = 0 */
export function developed(a: number, b: number, r2: number): { p: number; q: number; c: number } {
  return { p: -2 * a, q: -2 * b, c: a * a + b * b - r2 };
}

function term(coef: number, name: string): string {
  if (coef === 0) return '';
  const abs = Math.abs(coef);
  return `${coef < 0 ? ' - ' : ' + '}${abs === 1 && name ? '' : dec(abs)}${name}`;
}

export function developedTex(a: number, b: number, r2: number): string {
  const { p, q, c } = developed(a, b, r2);
  return `x^2 + y^2${term(p, 'x')}${term(q, 'y')}${term(c, '')} = 0`;
}

/** Les étapes « compléter les carrés », de la forme développée à la canonique. */
export function completingSteps(a: number, b: number, r2: number): string[] {
  const { c } = developed(a, b, r2);
  const part = (name: 'x' | 'y', v: number) =>
    v === 0 ? `${name}^2` : `${square(name, v)} - ${dec(v * v)}`;
  const lines = [developedTex(a, b, r2)];
  if (a !== 0 || b !== 0) lines.push(`${part('x', a)} + ${part('y', b)}${term(c, '')} = 0`);
  lines.push(canonicalTex(a, b, r2));
  return lines;
}

/** rayon exact : « 4 », « √13 », « 2√5 », « 2,5 » (r² décimal : r² = n/4) */
export function radiusExact(r2: number): {
  plain: string;
  tex: string;
  atoms: string[];
  value: number;
} {
  const n = Math.round(r2 * 4); // r = √n / 2
  const s = isqrt(n);
  if (s * s === n) {
    const e = rational(s, 2);
    return { plain: e.plain, tex: e.tex, atoms: e.atoms, value: e.value };
  }
  const { k, b } = simplify(n);
  // r = k√b / 2
  if (k % 2 === 0) {
    const kk = k / 2;
    const lead = kk === 1 ? '' : String(kk);
    return {
      plain: `${lead}√${b}`,
      tex: `${lead}\\sqrt{${b}}`,
      atoms: [...(lead ? [lead] : []), '√', String(b)],
      value: Math.sqrt(r2),
    };
  }
  const lead = k === 1 ? '' : String(k);
  return {
    plain: `${lead}√${b}/2`,
    tex: `\\frac{${lead}\\sqrt{${b}}}{2}`,
    atoms: [...(lead ? [lead] : []), '√', String(b), '÷', '2'],
    value: Math.sqrt(r2),
  };
}

export type Position = 'inside' | 'on' | 'outside';

export function position(a: number, b: number, r2: number, m: Pt): { d2: number; pos: Position } {
  const d2 = (m.x - a) ** 2 + (m.y - b) ** 2;
  const eps = 1e-9;
  return { d2, pos: Math.abs(d2 - r2) < eps ? 'on' : d2 < r2 ? 'inside' : 'outside' };
}

/** cercle de diamètre [AB] : centre = milieu, r² = AB² / 4 */
export function fromDiameter(A: Pt, B: Pt): { a: number; b: number; r2: number } {
  return {
    a: (A.x + B.x) / 2,
    b: (A.y + B.y) / 2,
    r2: ((B.x - A.x) ** 2 + (B.y - A.y) ** 2) / 4,
  };
}

/** (x − xA)(x − xB) + (y − yA)(y − yB) = 0 : MA · MB = 0 ; (x − 2)(x − 2) s'écrit (x − 2)² */
export function diameterTex(A: Pt, B: Pt): string {
  const f = (name: 'x' | 'y', u: number) => (u === 0 ? name : `(${shift(name, u)})`);
  const pair = (name: 'x' | 'y', u: number, v: number) =>
    u === v ? square(name, u) : `${f(name, u)}${f(name, v)}`;
  return `${pair('x', A.x, B.x)} + ${pair('y', A.y, B.y)} = 0`;
}

/** Équation lisible à voix haute : « (x − 2)² + (y + 1)² = 9 » */
export function canonicalPlain(a: number, b: number, r2: number): string {
  const sq = (name: 'x' | 'y', u: number) => {
    if (u === 0) return `${name}²`;
    const v = rational(Math.round(Math.abs(u) * 4), 4).plain;
    return `(${name} ${u > 0 ? '−' : '+'} ${v})²`;
  };
  return `${sq('x', a)} + ${sq('y', b)} = ${rational(Math.round(r2 * 4), 4).plain}`;
}

export interface Goal {
  text: string;
  check: (a: number, b: number, r2: number) => boolean;
}

export const GOALS: readonly Goal[] = [
  {
    text: 'Trace le cercle de centre (1 ; −2) et de rayon 3.',
    check: (a, b, r2) => a === 1 && b === -2 && r2 === 9,
  },
  {
    text: 'Trace le cercle centré à l’origine O qui passe par (3 ; 4).',
    check: (a, b, r2) => a === 0 && b === 0 && r2 === 25,
  },
  {
    text: 'Trace le cercle d’équation x² + y² − 4x + 2y − 4 = 0.',
    check: (a, b, r2) => a === 2 && b === -1 && r2 === 9,
  },
  {
    text: 'Trace un cercle de rayon √5 centré en (−1 ; 1).',
    check: (a, b, r2) => a === -1 && b === 1 && r2 === 5,
  },
];
